import React, { useEffect, useRef, useState, useContext } from 'react';
import * as S from '../BarChart/BarChart.style';
import * as d3 from 'd3';
import { useStore } from '../../../store/useStore';
import { ThemeContext } from 'styled-components';

type Props = {
  size: { width: number; height: number };
};

// Convert fraction to percent
const toPct = (frac: number | undefined): number => (frac ?? 0) * 100;

export const Acceleration = ({ size }: Props) => {
  const dataFeed = useStore((state) => state.dataFeed);
  const { acceleration, slope } = dataFeed || {};

  // Convert fractions to percentages
  const accPct = toPct(acceleration);
  const slopePct = toPct(slope);

  const [accelerations, setAccelerations] = useState<number[]>([]);
  const [slopes, setSlopes] = useState<number[]>([]);
  const theme = useContext(ThemeContext);
  const chartRef = useRef(null);

  const drawChart = () => {
    if (!theme) return;

    // set the dimensions and margins of the graph
    const margin = { top: 20, right: 30, bottom: 30, left: 40 },
      width = size.width - margin.left - margin.right,
      height = size.height - margin.top - margin.bottom;

    // Guard: Don't draw if size is invalid (negative or too small)
    if (width <= 0 || height <= 0 || !size.width || !size.height) {
      return;
    }

    d3.select('#acceleration').selectAll('svg').remove();

    // append the svg object to the body of the page
    const svg = d3
      .select('#acceleration')
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom - 20)
      .style('border', `5px solid ${theme.primary}`)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    const densityInput: [number, number][] = accelerations.map((a, i) => [a, slopes[i] ?? 0]);

    // Stable axis domains from buffered history
    const accExtent = d3.extent(accelerations) as [number, number];
    const slopeExtent = d3.extent(slopes) as [number, number];
    const accRange = accExtent[1] - accExtent[0];
    const slopeRange = slopeExtent[1] - slopeExtent[0];
    const accPad =
      Math.max(accRange * 0.3, Math.abs(accExtent[1] || accExtent[0] || 0) * 0.5) || 0.05;
    const slopePad =
      Math.max(slopeRange * 0.3, Math.abs(slopeExtent[1] || slopeExtent[0] || 0) * 0.5) || 0.05;

    // Add X axis (acceleration in %)
    const x = d3
      .scaleLinear()
      .domain([(accExtent[0] ?? -1) - accPad, (accExtent[1] ?? 1) + accPad])
      .range([0, width]);
    svg
      .append('g')
      .attr('transform', 'translate(0,' + height + ')')
      .call(d3.axisBottom(x).tickFormat((d) => `${d}%`));

    // Add Y axis (slope in %)
    const y = d3
      .scaleLinear()
      .domain([(slopeExtent[0] ?? -1) - slopePad, (slopeExtent[1] ?? 1) + slopePad])
      .range([height, 0]);
    svg.append('g').call(d3.axisLeft(y).tickFormat((d) => `${d}%`));

    // Bandwidth scales with data count - fewer points = more smoothing
    const bandwidth = Math.max(1, Math.floor(20 / Math.sqrt(densityInput.length || 1)));

    // compute the density data
    const densityFn = d3
      .contourDensity<[number, number]>()
      .x(function (d) {
        return x(d[0]);
      })
      .y(function (d) {
        return y(d[1]);
      })
      .size([width, height])
      .bandwidth(bandwidth);
    const densityData = densityFn(densityInput);

    // Add the contour: filled with graduated opacity
    const contourCount = densityData.length;
    svg
      .selectAll('path')
      .data(densityData)
      .enter()
      .append('path')
      .attr('d', d3.geoPath())
      .attr('fill', theme.primary)
      .attr('fill-opacity', (_d, i) => ((contourCount - i) / contourCount) * 0.5)
      .attr('stroke', theme.primary)
      .attr('stroke-opacity', 0.5)
      .attr('stroke-linejoin', 'round');

    // Current position marker with crosshairs
    if (accelerations.length > 0) {
      const latestAcc = accelerations[accelerations.length - 1];
      const latestSlope = slopes[slopes.length - 1] ?? 0;
      const cx = x(latestAcc);
      const cy = y(latestSlope);

      // Crosshair lines
      svg
        .append('line')
        .attr('x1', cx)
        .attr('y1', 0)
        .attr('x2', cx)
        .attr('y2', height)
        .attr('stroke', theme.text)
        .attr('stroke-width', 0.5)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0.4);
      svg
        .append('line')
        .attr('x1', 0)
        .attr('y1', cy)
        .attr('x2', width)
        .attr('y2', cy)
        .attr('stroke', theme.text)
        .attr('stroke-width', 0.5)
        .attr('stroke-dasharray', '3,3')
        .attr('opacity', 0.4);

      // Dot
      svg
        .append('circle')
        .attr('cx', cx)
        .attr('cy', cy)
        .attr('r', 6)
        .attr('fill', theme.secondary)
        .attr('stroke', theme.text)
        .attr('stroke-width', 1.5);
    }
  };

  useEffect(() => {
    setAccelerations([...accelerations.slice(-179), accPct]);
  }, [accPct]);

  useEffect(() => {
    setSlopes([...slopes.slice(-179), slopePct]);
  }, [slopePct]);

  useEffect(() => {
    drawChart();
  }, [accPct, slopePct, theme]);

  return (
    <S.ChartSpace>
      <div ref={chartRef} id="acceleration"></div>
    </S.ChartSpace>
  );
};
